import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Eye, EyeOff, Pencil, Plus, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { platformApi } from '../api'
import { Modal } from '../components/Modal'
import { StatusPill } from '../components/StatusPill'
import { usePlatformAuthStore } from '../store'
import type { PlanView, SavePlanRequest } from '../types'

const MODULES = [
  'CORE', 'PAYROLL', 'ATTENDANCE', 'LEAVE', 'RECRUITMENT', 'PERFORMANCE',
  'TRAINING', 'ASSET', 'EXPENSE', 'HELPDESK', 'COMPLIANCE', 'REPORTING',
]

/**
 * The plan catalogue, and the screen that changes it.
 *
 * <p>This used to be read-only, on the reasoning that a plan is a commercial decision rather
 * than a form. It is — but it was a commercial decision that needed a deploy, which is worse:
 * a price agreed in a meeting waited on a release. Repricing here is safe because
 * {@code subscription.unit_price_minor} is a snapshot, so nobody already on a plan moves.
 */
export function PlatformPlansPage() {
  const queryClient = useQueryClient()
  const role = usePlatformAuthStore((state) => state.operator?.role)
  const mayEdit = role === 'OWNER' || role === 'BILLING'

  const [editing, setEditing] = useState<PlanView | null>(null)
  const [creating, setCreating] = useState(false)
  const [deleting, setDeleting] = useState<PlanView | null>(null)
  const [error, setError] = useState<string | null>(null)

  const plans = useQuery({ queryKey: ['platform', 'plans'], queryFn: () => platformApi.plans() })

  function refresh() {
    void queryClient.invalidateQueries({ queryKey: ['platform', 'plans'] })
  }

  function onError(err: unknown) {
    const detail = (err as { response?: { data?: { detail?: string; title?: string } } }).response
      ?.data
    setError(detail?.detail ?? detail?.title ?? 'That did not work.')
  }

  const save = useMutation({
    mutationFn: ({ code, plan, isNew }: { code: string; plan: SavePlanRequest; isNew: boolean }) =>
      isNew ? platformApi.createPlan(code, plan) : platformApi.updatePlan(code, plan),
    onSuccess: () => {
      setEditing(null)
      setCreating(false)
      setError(null)
      refresh()
    },
    onError,
  })

  const setOffered = useMutation({
    mutationFn: ({ code, offered }: { code: string; offered: boolean }) =>
      platformApi.setPlanOffered(code, offered),
    onSuccess: () => {
      setError(null)
      refresh()
    },
    onError,
  })

  const remove = useMutation({
    mutationFn: (code: string) => platformApi.deletePlan(code),
    onSuccess: () => {
      setDeleting(null)
      setError(null)
      refresh()
    },
    onError,
  })

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center gap-4">
        <div>
          <h1 className="text-xl font-semibold text-[var(--color-heading)]">Plans</h1>
          <p className="text-sm text-[var(--color-muted)]">
            What each plan unlocks and where its ceilings are. A blank ceiling means unlimited.
          </p>
        </div>
        {mayEdit && (
          <Button className="ms-auto" onClick={() => setCreating(true)}>
            <Plus className="me-2 size-4" aria-hidden />
            New plan
          </Button>
        )}
      </div>

      {error && (
        <p
          role="alert"
          className="rounded-[var(--radius-card)] border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-900 dark:border-rose-900 dark:bg-rose-950 dark:text-rose-200"
        >
          {error}
        </p>
      )}

      <div className="grid gap-4 lg:grid-cols-3">
        {(plans.data ?? []).map((plan) => (
          <article
            key={plan.code}
            className="flex flex-col rounded-[var(--radius-card)] border border-[var(--color-border)] bg-[var(--color-card)] p-5"
          >
            <header className="mb-3 flex items-start gap-2">
              <div>
                <h2 className="text-base font-semibold text-[var(--color-heading)]">{plan.name}</h2>
                <p className="text-sm text-[var(--color-muted)]">{plan.description}</p>
              </div>
              <div className="ms-auto">
                {plan.isPublic ? (
                  <StatusPill tone="good">offered</StatusPill>
                ) : (
                  <StatusPill tone="neutral">withdrawn</StatusPill>
                )}
              </div>
            </header>

            <p className="mb-4 text-2xl font-semibold tabular-nums text-[var(--color-heading)]">
              {plan.priceMinor === 0
                ? 'Negotiated'
                : `${plan.currency} ${(plan.priceMinor / 100).toLocaleString()}`}
              {plan.priceMinor > 0 && (
                <span className="text-sm font-normal text-[var(--color-muted)]">
                  {' '}
                  / {plan.billingPeriod === 'ANNUAL' ? 'year' : 'month'}
                </span>
              )}
            </p>

            <dl className="mb-4 space-y-1 text-sm">
              <Limit label="Employees" value={plan.maxEmployees} />
              <Limit label="Users" value={plan.maxUsers} />
              <Limit
                label="Storage"
                value={plan.maxStorageMb}
                format={(mb) => `${(mb / 1024).toFixed(0)} GB`}
              />
              <div className="flex justify-between">
                <dt className="text-[var(--color-muted)]">Trial</dt>
                <dd>{plan.trialDays > 0 ? `${plan.trialDays} days` : 'none'}</dd>
              </div>
            </dl>

            <div className="mb-4 flex flex-wrap gap-1">
              {plan.modules.map((module) => (
                <span
                  key={module}
                  className="rounded bg-[var(--color-page)] px-2 py-0.5 text-xs text-[var(--color-body)]"
                >
                  {module.toLowerCase()}
                </span>
              ))}
            </div>

            {mayEdit && (
              <div className="mt-auto flex flex-wrap gap-2 border-t border-[var(--color-border)] pt-3">
                <Button size="sm" variant="ghost" onClick={() => setEditing(plan)}>
                  <Pencil className="me-1.5 size-4" aria-hidden />
                  Edit
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setOffered.mutate({ code: plan.code, offered: !plan.isPublic })}
                >
                  {plan.isPublic ? (
                    <>
                      <EyeOff className="me-1.5 size-4" aria-hidden />
                      Withdraw
                    </>
                  ) : (
                    <>
                      <Eye className="me-1.5 size-4" aria-hidden />
                      Offer
                    </>
                  )}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setDeleting(plan)}>
                  <Trash2 className="me-1.5 size-4" aria-hidden />
                  Delete
                </Button>
              </div>
            )}
          </article>
        ))}
      </div>

      {mayEdit && (
        <p className="text-xs text-[var(--color-muted)]">
          Changing a price applies to new subscriptions only. A customer keeps the price they
          were sold, because the subscription stores it rather than reading it back from here.
        </p>
      )}

      <PlanModal
        open={creating || editing !== null}
        plan={editing}
        pending={save.isPending}
        onClose={() => {
          setCreating(false)
          setEditing(null)
        }}
        onSubmit={(code, plan, isNew) => save.mutate({ code, plan, isNew })}
      />

      <DeleteModal
        plan={deleting}
        pending={remove.isPending}
        onClose={() => setDeleting(null)}
        onConfirm={() => deleting && remove.mutate(deleting.code)}
      />
    </div>
  )
}

function PlanModal({
  open,
  plan,
  pending,
  onClose,
  onSubmit,
}: {
  open: boolean
  plan: PlanView | null
  pending: boolean
  onClose: () => void
  onSubmit: (code: string, plan: SavePlanRequest, isNew: boolean) => void
}) {
  const isNew = plan === null
  const [code, setCode] = useState('')
  const [form, setForm] = useState<SavePlanRequest>(blank())
  const [ready, setReady] = useState<string | null>(null)

  // Load the plan being edited once per open, without a useEffect chain.
  const key = plan?.code ?? '__new__'
  if (open && ready !== key) {
    setReady(key)
    setCode(plan?.code ?? '')
    setForm(plan ? fromPlan(plan) : blank())
  }
  if (!open && ready !== null) setReady(null)

  function set<K extends keyof SavePlanRequest>(field: K, value: SavePlanRequest[K]) {
    setForm((current) => ({ ...current, [field]: value }))
  }

  function toggleModule(module: string) {
    const current = form.modules ?? []
    set(
      'modules',
      current.includes(module)
        ? current.filter((item) => item !== module)
        : [...current, module],
    )
  }

  return (
    <Modal
      open={open}
      title={isNew ? 'New plan' : `Edit ${plan?.name}`}
      description={
        isNew
          ? 'The code is permanent — every subscription and invoice references it.'
          : 'Repricing applies to new subscriptions only.'
      }
      onClose={onClose}
      footer={
        <>
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="button"
            disabled={pending || !form.name.trim() || (isNew && !code.trim())}
            onClick={() => onSubmit(isNew ? code.trim() : plan.code, form, isNew)}
          >
            {pending ? 'Saving…' : isNew ? 'Create plan' : 'Save changes'}
          </Button>
        </>
      }
    >
      {isNew && (
        <div className="space-y-2">
          <Label htmlFor="plan-code">Code</Label>
          <Input
            id="plan-code"
            value={code}
            onChange={(event) => setCode(event.target.value.toUpperCase())}
            placeholder="PROFESSIONAL"
          />
        </div>
      )}

      <div className="space-y-2">
        <Label htmlFor="plan-name">Name</Label>
        <Input
          id="plan-name"
          value={form.name}
          onChange={(event) => set('name', event.target.value)}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="plan-description">Description</Label>
        <Input
          id="plan-description"
          value={form.description ?? ''}
          onChange={(event) => set('description', event.target.value)}
        />
      </div>

      <div className="grid grid-cols-3 gap-3">
        <div className="space-y-2">
          <Label htmlFor="plan-price">Price (major)</Label>
          <Input
            id="plan-price"
            type="number"
            min={0}
            value={form.priceMinor / 100}
            onChange={(event) => set('priceMinor', Math.round(Number(event.target.value) * 100))}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="plan-currency">Currency</Label>
          <Input
            id="plan-currency"
            maxLength={3}
            value={form.currency}
            onChange={(event) => set('currency', event.target.value.toUpperCase())}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="plan-period">Billed</Label>
          <select
            id="plan-period"
            value={form.billingPeriod}
            onChange={(event) =>
              set('billingPeriod', event.target.value as SavePlanRequest['billingPeriod'])
            }
            className="h-9 w-full rounded-md border border-[var(--color-border)] bg-[var(--color-card)] px-2 text-sm"
          >
            <option value="MONTHLY">Monthly</option>
            <option value="ANNUAL">Annually</option>
          </select>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <NullableNumber
          id="plan-employees"
          label="Max employees"
          value={form.maxEmployees}
          onChange={(value) => set('maxEmployees', value)}
        />
        <NullableNumber
          id="plan-users"
          label="Max users"
          value={form.maxUsers}
          onChange={(value) => set('maxUsers', value)}
        />
        <NullableNumber
          id="plan-storage"
          label="Storage MB"
          value={form.maxStorageMb}
          onChange={(value) => set('maxStorageMb', value)}
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-2">
          <Label htmlFor="plan-trial">Trial days</Label>
          <Input
            id="plan-trial"
            type="number"
            min={0}
            value={form.trialDays}
            onChange={(event) => set('trialDays', Number(event.target.value))}
          />
        </div>
        <label className="flex items-end gap-2 pb-2 text-sm">
          <input
            type="checkbox"
            checked={form.isPublic}
            onChange={(event) => set('isPublic', event.target.checked)}
          />
          Offer this plan to new customers
        </label>
      </div>

      <fieldset className="space-y-2">
        <legend className="text-sm font-medium text-[var(--color-heading)]">Modules</legend>
        <div className="flex flex-wrap gap-2">
          {MODULES.map((module) => {
            const on = (form.modules ?? []).includes(module)
            return (
              <button
                key={module}
                type="button"
                onClick={() => toggleModule(module)}
                aria-pressed={on}
                className={[
                  'rounded-full border px-3 py-1 text-xs transition-colors',
                  on
                    ? 'border-[var(--color-primary)] bg-[var(--color-primary)] text-white'
                    : 'border-[var(--color-border)] text-[var(--color-muted)]',
                ].join(' ')}
              >
                {module.toLowerCase()}
              </button>
            )
          })}
        </div>
      </fieldset>
    </Modal>
  )
}

function DeleteModal({
  plan,
  pending,
  onClose,
  onConfirm,
}: {
  plan: PlanView | null
  pending: boolean
  onClose: () => void
  onConfirm: () => void
}) {
  return (
    <Modal
      open={plan !== null}
      title={`Delete ${plan?.name ?? ''}?`}
      description="Only a plan nobody has ever subscribed to can be deleted. If anyone has been on it, withdraw it instead — the billing history has to keep naming it."
      onClose={onClose}
      footer={
        <>
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="button" variant="danger" disabled={pending} onClick={onConfirm}>
            {pending ? 'Deleting…' : 'Delete plan'}
          </Button>
        </>
      }
    >
      <p className="text-sm text-[var(--color-muted)]">
        This cannot be undone.
      </p>
    </Modal>
  )
}

function NullableNumber({
  id,
  label,
  value,
  onChange,
}: {
  id: string
  label: string
  value: number | null
  onChange: (value: number | null) => void
}) {
  return (
    <div className="space-y-2">
      <Label htmlFor={id}>{label}</Label>
      <Input
        id={id}
        type="number"
        min={1}
        value={value ?? ''}
        placeholder="Unlimited"
        onChange={(event) =>
          onChange(event.target.value === '' ? null : Number(event.target.value))
        }
      />
    </div>
  )
}

function blank(): SavePlanRequest {
  return {
    name: '',
    description: '',
    priceMinor: 0,
    currency: 'AED',
    billingPeriod: 'MONTHLY',
    maxEmployees: null,
    maxUsers: null,
    maxStorageMb: null,
    trialDays: 14,
    isPublic: true,
    sortOrder: 100,
    modules: ['CORE'],
  }
}

function fromPlan(plan: PlanView): SavePlanRequest {
  return {
    name: plan.name,
    description: plan.description,
    priceMinor: plan.priceMinor,
    currency: plan.currency,
    billingPeriod: plan.billingPeriod,
    maxEmployees: plan.maxEmployees,
    maxUsers: plan.maxUsers,
    maxStorageMb: plan.maxStorageMb,
    trialDays: plan.trialDays,
    isPublic: plan.isPublic,
    sortOrder: plan.sortOrder,
    modules: plan.modules,
  }
}

function Limit({
  label,
  value,
  format,
}: {
  label: string
  value: number | null
  format?: (value: number) => string
}) {
  return (
    <div className="flex justify-between">
      <dt className="text-[var(--color-muted)]">{label}</dt>
      <dd className="tabular-nums">
        {value == null ? 'Unlimited' : format ? format(value) : value.toLocaleString()}
      </dd>
    </div>
  )
}
